/**
 * Billing — the app subscription itself, not a payout.
 *
 * WHY the platform guard runs before any request: billing is Shopify's Billing API under the
 * hood, so the API answers HTTP 409 for a TikTok or Amazon store. Asking anyway would turn a
 * known, permanent fact ("this channel has no app charge") into a spurious error state, so the
 * component never calls `api.billing` for a store it already knows cannot have one.
 */
import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type Subscription } from "../api";
import { billingView, intervalLabel, returnNotice } from "../billing";
import { Amount } from "../components/Amount";
import { StatusBadge } from "../components/Badges";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { formatDateTime } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

/** The only platform the Billing API charges today (Shopify Billing API, `docs/reference/DECISIONS.md`). */
const BILLABLE_PLATFORM = "SHOPIFY";

export function Billing(): ReactNode {
  const store = useStore();

  if (store.platform !== BILLABLE_PLATFORM) {
    return (
      <EmptyState
        title="No app subscription for this channel"
        hint="Billing runs on the Shopify Billing API. Only Shopify stores can be charged for PayoutAudit today; TikTok and Amazon stores are not billed."
      />
    );
  }

  return <ShopifyBilling storeId={store.id} />;
}

function ShopifyBilling({ storeId }: { storeId: string }): ReactNode {
  const [params] = useSearchParams();
  const state = useAsync(() => api.billing(storeId), [storeId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  const notice = returnNotice(params.get("billing"));

  if (state.status === "loading") return <Loading label="Loading billing" />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.reload} />;
  const { plan, subscription } = state.data;
  const view = billingView(subscription);

  async function run(name: string, action: () => Promise<void>): Promise<void> {
    setBusy(name);
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }

  const subscribe = () => run("subscribe", async () => {
    const res = await api.subscribe(storeId);
    window.location.assign(res.confirmation_url);
  });

  // The confirm is asked BEFORE the action starts: declining is not a cancelled request, and
  // flashing "Cancelling…" at someone who just said no is a small lie about what happened.
  const cancel = () => {
    if (!window.confirm("Cancel this subscription? You will lose entitlement immediately.")) return;
    void run("cancel", async () => {
      await api.cancelSubscription(storeId);
      state.reload();
    });
  };

  // Two steps on purpose. `refresh: true` is a WRITE as much as a read — it makes the API re-query
  // Shopify and store the answer, because our `app_subscription` row is only a projection and
  // Shopify does not push us every change. The reload then re-renders from that updated row, which
  // is also what every other visitor to this screen will now see.
  const refresh = () => run("refresh", async () => {
    await api.billing(storeId, { refresh: true });
    state.reload();
  });

  return (
    <>
      <header className="page-head">
        <h1 className="page-title">Billing</h1>
      </header>

      {notice ? (
        <div className={`notice notice-${notice.tone}`} role="status">
          <p className="notice-title">{notice.title}</p>
          <p className="notice-text">{notice.text}</p>
        </div>
      ) : null}

      <div className="split">
        <section className="card">
          <h2 className="card-title">Plan</h2>
          <dl className="kv">
            <div><dt>Name</dt><dd>{plan.name}</dd></div>
            <div>
              <dt>Price</dt>
              <dd><Amount minor={plan.price_minor} currency={plan.currency} strong /> {intervalLabel(plan.billing_interval)}</dd>
            </div>
            {plan.trial_days > 0 ? (
              <div><dt>Free trial</dt><dd>{plan.trial_days} days</dd></div>
            ) : null}
          </dl>
        </section>

        <section className="card">
          <h2 className="card-title">Subscription</h2>
          {subscription ? <StatusBadge status={subscription.status} /> : null}
          <p className="state-title">{view.headline}</p>
          <p className="state-hint">{view.detail}</p>
          {subscription ? <SubscriptionFacts sub={subscription} /> : null}

          {subscription?.is_test ? (
            <div className="notice notice-warn">
              <p className="notice-title">Shopify TEST charge</p>
              <p className="notice-text">This subscription was created as a Shopify test charge. It exists in Shopify Admin, but nobody is billed.</p>
            </div>
          ) : null}

          <div className="actions">
            {view.approvalUrl ? (
              <a className="btn btn-primary" href={view.approvalUrl}>Finish approval in Shopify</a>
            ) : null}
            {view.canSubscribe ? (
              <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={subscribe}>
                {busy === "subscribe" ? "Subscribing…" : "Subscribe"}
              </button>
            ) : null}
            {view.canCancel ? (
              <button type="button" className="btn" disabled={busy !== null} onClick={cancel}>
                {busy === "cancel" ? "Cancelling…" : "Cancel subscription"}
              </button>
            ) : null}
            <button type="button" className="btn" disabled={busy !== null} onClick={refresh}>
              {busy === "refresh" ? "Refreshing…" : "Refresh from Shopify"}
            </button>
          </div>
          {actionError ? <p className="form-error">{actionError.message}</p> : null}
        </section>
      </div>
    </>
  );
}

function SubscriptionFacts({ sub }: { sub: Subscription }): ReactNode {
  return (
    <dl className="kv">
      <div><dt>Plan</dt><dd>{sub.plan_name}</dd></div>
      <div>
        <dt>Price</dt>
        <dd><Amount minor={sub.price_minor} currency={sub.currency} strong /> {intervalLabel(sub.billing_interval)}</dd>
      </div>
      {sub.trial_ends_at ? <div><dt>Trial ends</dt><dd>{formatDateTime(sub.trial_ends_at)}</dd></div> : null}
      {sub.current_period_end ? <div><dt>Period ends</dt><dd>{formatDateTime(sub.current_period_end)}</dd></div> : null}
      {sub.activated_at ? <div><dt>Approved</dt><dd>{formatDateTime(sub.activated_at)}</dd></div> : null}
      {sub.cancelled_at ? <div><dt>Cancelled</dt><dd>{formatDateTime(sub.cancelled_at)}</dd></div> : null}
    </dl>
  );
}
